'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEvent, useLatest, useUnmount } from 'react-use';
import { A11y, Keyboard, Mousewheel, Pagination } from 'swiper/modules';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';

import { useConfigValue } from '@/components/providers/config-provider';
import { Button } from '@/components/ui/button';
import { useIsTouchDevice } from '@/hooks/use-mobile';
import { usePageDirection } from '@/hooks/use-page-direction';
import { useParentPointerUp } from '@/hooks/use-parent-pointer-up';
import { cn } from '@/lib/utils';
import { prefersReducedMotion } from '@/lib/utils/motion';
import { createDebouncedResizeObserver } from '@/lib/utils/resize-observer';

import type { Swiper as SwiperClass, SwiperOptions } from 'swiper/types';

import 'swiper/css';
import 'swiper/css/pagination';

export type CarouselApi = SwiperClass | null;

type DragPhase = 'idle' | 'touching' | 'momentum';

const MODULES_WITHOUT_PAGINATION = [A11y, Keyboard, Mousewheel];
const MODULES_WITH_PAGINATION = [...MODULES_WITHOUT_PAGINATION, Pagination];

const KEYBOARD_CONFIG = { enabled: true };
const A11Y_CONFIG = {
  enabled: true,
  containerRoleDescriptionMessage: 'carousel',
  itemRoleDescriptionMessage: 'slide',
};
const MOUSEWHEEL_CONFIG = {
  forceToAxis: true,
  thresholdDelta: 10,
  thresholdTime: 500,
  sensitivity: 1,
  releaseOnEdges: false,
  noMousewheelClass: 'swiper-no-mousewheel',
};
const PAGINATION_CONFIG = {
  clickable: true,
  dynamicBullets: true,
  dynamicMainBullets: 3,
};

const SLIDE_OPACITY_MIN = 0.4;

// Delay before enabling CSS transitions, allowing initial state to be painted first.
// Prevents animation flash on mount. 50ms allows for browser paint cycle.
const TRANSITION_READY_DELAY_MS = 50;

// In cssMode (touch), momentum scroll continues after finger lift.
// This debounce detects when scrolling has settled to clean up drag state.
export const SCROLL_SETTLE_MS = 600;

interface CarouselProps {
  opts?: SwiperOptions;
  setApi?: (api: CarouselApi) => void;
  onSlideChange?: (swiper: SwiperClass, isDragging: boolean) => void;
  onProgress?: (swiper: SwiperClass, progress: number) => void;
  onReachEnd?: (swiper: SwiperClass) => void;
  initialSlide?: number;
  showPagination?: boolean;
  /** Prev/next arrows. Each side fades out once there is nothing further that way. */
  showNavigation?: boolean;
}

/**
 * A remount destroys Swiper, and `destroy()` deletes the instance's own properties — so the ref
 * still holds an object whose `slides` is gone, and a truthy check passes. Anything scheduled
 * before the remount has to come back through here. `slides` is typed as a plain array, which is
 * why touching a destroyed instance compiles.
 */
function liveSwiper(swiper: SwiperClass | null): SwiperClass | undefined {
  return swiper && !swiper.destroyed ? swiper : undefined;
}

function Carousel({
  opts,
  setApi,
  onSlideChange,
  onProgress,
  onReachEnd,
  initialSlide = 0,
  showPagination = false,
  showNavigation = false,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const pageDir = usePageDirection();
  const modules = showPagination ? MODULES_WITH_PAGINATION : MODULES_WITHOUT_PAGINATION;
  const pagination = showPagination ? PAGINATION_CONFIG : false;
  const [isInitialized, setIsInitialized] = useState(false);
  const [transitionsReady, setTransitionsReady] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [swiperEl, setSwiperEl] = useState<HTMLElement | null>(null);
  const [wrapperEl, setWrapperEl] = useState<HTMLElement | null>(null);
  const swiperRef = useRef<SwiperClass | null>(null);
  const dragPhaseRef = useRef<DragPhase>('idle');
  const scrollSettleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const opacityFrameRef = useRef<number | undefined>(undefined);
  const isTouchDevice = useIsTouchDevice();
  const onSlideChangeLatest = useLatest(onSlideChange);
  const onProgressLatest = useLatest(onProgress);
  const prevNavRef = useRef<HTMLButtonElement>(null);
  const nextNavRef = useRef<HTMLButtonElement>(null);

  /**
   * Toggles `is-hidden` on the DOM rather than through React state: a re-render hands `<Swiper>`
   * a fresh `children` identity, which makes swiper/react call `swiper.update()` — and doing that
   * mid-transition visibly breaks the edge snap-back.
   */
  const syncNavState = useCallback((swiper: SwiperClass) => {
    prevNavRef.current?.classList.toggle('is-hidden', swiper.isLocked || swiper.isBeginning);
    nextNavRef.current?.classList.toggle('is-hidden', swiper.isLocked || swiper.isEnd);
  }, []);

  /**
   * Precomputed snap positions and direction-aware step distances for each slide.
   * Built once at drag start via {@link snapshotSlideGrid} and read on every
   * frame by {@link applySlideOpacity}. This avoids repeated `snapGrid`/`slidesGrid`
   * fallback lookups and step calculations in the hot path (~60fps during drag).
   *
   * Uses `snapGrid` (not `slidesGrid`) because with `centeredSlides` the snap
   * positions account for centering offset, ensuring opacity=1.0 at each slide's
   * exact rest position — matching the CSS `.swiper-slide-active { opacity: 1 }`
   * with no visible jump on drag start.
   *
   * Steps are direction-aware (left vs right neighbor) because `centeredSlidesBounds`
   * produces asymmetric snap gaps at the edges (e.g., first gap = 428px, middle
   * gaps = 511px). A uniform step would cause neighboring slides to start at
   * the wrong opacity.
   */
  const slideGridRef = useRef<{ pos: number; leftStep: number; rightStep: number }[]>([]);

  const snapshotSlideGrid = useCallback((swiper: SwiperClass) => {
    const { snapGrid, slidesGrid, slides } = swiper;
    const len = slides.length;
    const grid = new Array<{ pos: number; leftStep: number; rightStep: number }>(len);
    for (let i = 0; i < len; i++) {
      const pos = snapGrid[i] ?? slidesGrid[i] ?? 0;
      const prev = i > 0 ? (snapGrid[i - 1] ?? slidesGrid[i - 1] ?? 0) : undefined;
      const next = i < len - 1 ? (snapGrid[i + 1] ?? slidesGrid[i + 1] ?? 0) : undefined;
      grid[i] = {
        pos,
        leftStep: prev != null ? pos - prev : next != null ? next - pos : 1,
        rightStep: next != null ? next - pos : prev != null ? pos - prev : 1,
      };
    }
    slideGridRef.current = grid;
  }, []);

  const applySlideOpacity = useCallback((swiper: SwiperClass) => {
    const { slides, rtlTranslate, translate } = swiper;
    const offsetCenter = rtlTranslate ? translate : -translate;
    const opacityRange = 1 - SLIDE_OPACITY_MIN;
    const grid = slideGridRef.current;

    for (let i = 0; i < slides.length; i++) {
      const entry = grid[i];
      if (!entry) break;
      const { pos, leftStep, rightStep } = entry;
      const step = offsetCenter <= pos ? leftStep : rightStep;
      const p = Math.abs((offsetCenter - pos) / step);
      (slides[i] as HTMLElement).style.opacity =
        `${Math.max(SLIDE_OPACITY_MIN, 1 - opacityRange * p)}`;
    }
  }, []);

  const clearSlideOpacity = useCallback((swiper: SwiperClass) => {
    for (const slide of swiper.slides) {
      (slide as HTMLElement).style.opacity = '';
    }
  }, []);

  const handleSwiper = useCallback(
    (swiper: SwiperClass) => {
      /**
       * Guard swiper.update() during active drag to prevent image-load stutter.
       * Swiper internally calls onLoad → update() → slideTo(activeIndex) when
       * a slide image finishes loading. During drag this snaps the translate
       * back to the active slide position, causing a visible jump. Skipping
       * update() while dragging is safe because Swiper recalculates layout
       * on the post-drag slideTo() transition anyway.
       */
      const originalUpdate = swiper.update.bind(swiper);
      swiper.update = function (...args: Parameters<SwiperClass['update']>) {
        if (dragPhaseRef.current !== 'idle') return;
        originalUpdate(...args);
      };

      swiperRef.current = swiper;
      setWrapperEl(swiper.wrapperEl);
      setSwiperEl(swiper.el);
      setApi?.(swiper);
      syncNavState(swiper);
      setIsInitialized(true);
    },
    [setApi, syncNavState],
  );

  useEffect(() => {
    if (!isInitialized) return;
    const timer = setTimeout(() => setTransitionsReady(true), TRANSITION_READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isInitialized]);

  useEffect(() => {
    swiperRef.current?.changeLanguageDirection(pageDir);
  }, [pageDir]);

  const endDrag = useCallback(() => {
    dragPhaseRef.current = 'idle';
    setIsTransitioning(false);

    // Defer clearing inline opacity so the CSS transition (re-enabled by
    // removing carousel-transitioning) can animate from drag values to
    // the CSS class values.
    opacityFrameRef.current = requestAnimationFrame(() => {
      const swiper = liveSwiper(swiperRef.current);
      if (swiper) clearSlideOpacity(swiper);
    });
  }, [clearSlideOpacity]);

  const scheduleSettleClear = useCallback(() => {
    clearTimeout(scrollSettleTimerRef.current);
    scrollSettleTimerRef.current = setTimeout(() => {
      // Force snap correction — CSS scroll-snap on iOS can occasionally fail to land exactly on a
      // snap point. speed=0 avoids a visible animation; Swiper's default speed would smooth-scroll
      // the already-settled slide.
      // See: https://github.com/nolimits4web/swiper/issues/5507
      const swiper = liveSwiper(swiperRef.current);
      if (swiper && isTouchDevice) {
        swiper.slideTo(swiper.activeIndex, 0);
      }
      endDrag();
    }, SCROLL_SETTLE_MS);
  }, [endDrag, isTouchDevice]);

  // scrollend fires after momentum scroll AND CSS snap fully settle — more reliable than the
  // fixed timer. Falls back to the timer on iOS < 16.4 that lacks scrollend support.
  //
  // Guard: only act during 'momentum' (finger already lifted). In cssMode the browser fires
  // scrollend on the wrapper whenever any scroll stops — including brief mid-gesture pauses while
  // the finger is still down ('touching'). Calling endDrag() then would snap the slide and clear
  // drag state while the user is still swiping, causing a visible shake.
  //
  // No snap correction here — CSS snap has already settled. Re-calling slideTo() (even to the
  // same index) causes iOS to reassign this element as the active scroll context, so the next
  // vertical swipe on the parent thread is consumed first, requiring the user to swipe twice.
  useEvent(
    'scrollend',
    () => {
      if (dragPhaseRef.current !== 'momentum') return;
      clearTimeout(scrollSettleTimerRef.current);
      endDrag();
    },
    isTouchDevice ? wrapperEl : null,
  );

  const handleSwiperDragStart = useCallback(() => {
    clearTimeout(scrollSettleTimerRef.current);
  }, []);

  const handleSliderMove = useCallback(() => {
    if (dragPhaseRef.current !== 'idle') return;
    if (swiperRef.current) snapshotSlideGrid(swiperRef.current);
    dragPhaseRef.current = 'touching';
    setIsTransitioning(true);
  }, [snapshotSlideGrid]);

  const handleProgress = useCallback(
    (swiper: SwiperClass) => {
      if (dragPhaseRef.current !== 'idle') {
        applySlideOpacity(swiper);

        if (dragPhaseRef.current === 'momentum') {
          scheduleSettleClear();
        }
      }
      onProgressLatest.current?.(swiper, swiper.progress);
    },
    [applySlideOpacity, scheduleSettleClear, onProgressLatest],
  );

  const handleSwiperDragEnd = useCallback(() => {
    if (isTouchDevice) {
      if (dragPhaseRef.current === 'idle') return;
      dragPhaseRef.current = 'momentum';
      scheduleSettleClear();
      return;
    }
    endDrag();
  }, [isTouchDevice, endDrag, scheduleSettleClear]);

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => onSlideChangeLatest.current?.(swiper, dragPhaseRef.current !== 'idle'),
    [onSlideChangeLatest],
  );

  useUnmount(() => {
    clearTimeout(scrollSettleTimerRef.current);
    if (opacityFrameRef.current !== undefined) cancelAnimationFrame(opacityFrameRef.current);
  });

  useParentPointerUp(isTouchDevice ? null : swiperEl);

  // Dots sit in normal flow inside .swiper, so the carousel box is taller than the slides; CSS
  // subtracts this to centre the arrows on the slides. Measured, not hardcoded — the rendered
  // height comes from Swiper's own bullet styles, and a literal was 9px out.
  const [dotsSpace, setDotsSpace] = useState(0);
  useEffect(() => {
    const dots = showNavigation ? swiperEl?.querySelector<HTMLElement>('.swiper-pagination') : null;
    if (!dots) {
      setDotsSpace(0);
      return;
    }
    // observe() delivers an initial callback, so this doubles as the first measurement.
    const { observer, disconnect } = createDebouncedResizeObserver(() =>
      setDotsSpace(dots.offsetHeight + (parseFloat(getComputedStyle(dots).marginTop) || 0)),
    );
    observer.observe(dots);
    return disconnect;
  }, [swiperEl, showPagination, showNavigation]);

  // The buttons only exist after the init re-render, so the very first sync has to run here —
  // `handleSwiper` fires while the refs are still empty.
  useEffect(() => {
    if (swiperRef.current) syncNavState(swiperRef.current);
  }, [isInitialized, showNavigation, syncNavState]);

  const t = useTranslations('thread.carousel');
  const iconType = useConfigValue<'chevron' | 'arrow'>(
    'branding.components.carouselButton.iconType',
  );
  // Read on click, not via a hook: a subscription per carousel costs a mount-time render, and
  // every render hands <Swiper> new children (see syncNavState).
  const slide = (direction: 'prev' | 'next') => {
    const speed = prefersReducedMotion() ? 0 : undefined;
    const swiper = swiperRef.current;
    if (direction === 'prev') swiper?.slidePrev(speed);
    else swiper?.slideNext(speed);
  };

  // slidePrev/slideNext are direction-agnostic; only the glyph has to flip.
  const [back, forward] =
    iconType === 'arrow' ? [ArrowLeftIcon, ArrowRightIcon] : [ChevronLeftIcon, ChevronRightIcon];
  const [PrevIcon, NextIcon] = pageDir === 'rtl' ? [forward, back] : [back, forward];

  return (
    <div
      className={cn(
        'bzk-carousel relative',
        isInitialized && 'carousel-initialized',
        transitionsReady && 'carousel-transitions-ready',
        isTransitioning && 'carousel-transitioning',
        className,
      )}
      role="region"
      aria-roledescription="carousel"
      data-slot="carousel"
      {...props}
      style={{ ['--carousel-slides-offset' as string]: `${dotsSpace}px`, ...props.style }}
    >
      <SwiperComponent
        modules={modules}
        keyboard={KEYBOARD_CONFIG}
        mousewheel={MOUSEWHEEL_CONFIG}
        a11y={A11Y_CONFIG}
        cssMode={isTouchDevice}
        edgeSwipeDetection="prevent"
        touchStartPreventDefault={!isTouchDevice}
        passiveListeners={isTouchDevice}
        pagination={pagination}
        dir={pageDir}
        initialSlide={initialSlide}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        onProgress={handleProgress}
        onReachEnd={onReachEnd}
        onTouchStart={handleSwiperDragStart}
        onSliderMove={handleSliderMove}
        onTouchEnd={handleSwiperDragEnd}
        // Swiper's purpose-built edge/overflow signals, all emitted from updateProgress — so they
        // cover drag, scroll, resize and item changes without running on every frame.
        onToEdge={syncNavState}
        onFromEdge={syncNavState}
        onLock={syncNavState}
        onUnlock={syncNavState}
        {...opts}
      >
        {children}
      </SwiperComponent>

      {showNavigation &&
        (
          [
            { side: 'prev', ref: prevNavRef, Icon: PrevIcon },
            { side: 'next', ref: nextNavRef, Icon: NextIcon },
          ] as const
        ).map(({ side, ref, Icon }) => (
          <Button
            key={side}
            ref={ref}
            type="button"
            variant="carouselNav"
            size="icon"
            className={`carousel-nav carousel-nav-${side} is-hidden`}
            aria-label={t(side === 'prev' ? 'previous' : 'next')}
            onClick={() => slide(side)}
          >
            <Icon aria-hidden="true" />
          </Button>
        ))}
    </div>
  );
}

export { type SwiperClass, Carousel, SwiperSlide };
